import React from "react";
import { CheckIcon } from "@heroicons/react/16/solid";

const formatPrice = (value) => value.toFixed(2).replace(".", ",");

const parseStartDate = (value) => {
	if (!value || typeof value !== "string") return null;
	const trimmed = value.trim();
	const ddmmyyyy = /^(\d{2})-(\d{2})-(\d{4})$/;
	const yyyymmdd = /^(\d{4})-(\d{2})-(\d{2})$/;

	let day;
	let month;
	let year;

	let match = trimmed.match(ddmmyyyy);
	if (match) {
		day = parseInt(match[1], 10);
		month = parseInt(match[2], 10);
		year = parseInt(match[3], 10);
	} else {
		match = trimmed.match(yyyymmdd);
		if (!match) return null;
		year = parseInt(match[1], 10);
		month = parseInt(match[2], 10);
		day = parseInt(match[3], 10);
	}

	if (
		Number.isNaN(day) ||
		Number.isNaN(month) ||
		Number.isNaN(year) ||
		day < 1 ||
		day > 31 ||
		month < 1 ||
		month > 12
	) {
		return null;
	}

	const d = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
	return Number.isNaN(d.getTime()) ? null : d;
};

const formatNlDate = (date) =>
	date.toLocaleDateString("nl-NL", {
		day: "numeric",
		month: "long",
		year: "numeric",
	});

const FreeTrialOrderSummary = ({ planInfo, values, couponCode }) => {
	// Calculate pricing details (all amounts are provided by the backend)
	const netPrice = Number.isFinite(planInfo?.finalPrice)
		? planInfo.finalPrice
		: planInfo?.price || 29.99;

	const taxAmount = Number.isFinite(planInfo?.taxAmount)
		? planInfo.taxAmount
		: 0;

	const totalPrice = Number.isFinite(planInfo?.totalPrice)
		? planInfo.totalPrice
		: netPrice + taxAmount;

	const trialDays = Number.isFinite(planInfo?.trialDays)
		? planInfo.trialDays
		: 0;

	const startDateLabel = (() => {
		// Prefer backend-derived billingStartsAt
		if (Number.isFinite(planInfo?.billingStartsAt)) {
			const d = new Date(planInfo.billingStartsAt * 1000);
			if (!Number.isNaN(d.getTime())) return formatNlDate(d);
		}

		// Fallback: parse startDate string if present
		const fromString = parseStartDate(planInfo?.startDate);
		if (fromString) return formatNlDate(fromString);

		// Last resort: derive from trialDays
		if (trialDays > 0) {
			const d = new Date(Date.now() + trialDays * 24 * 60 * 60 * 1000);
			if (!Number.isNaN(d.getTime())) return formatNlDate(d);
		}

		return null;
	})();

	if (totalPrice === 0) {
		return null;
	}

	return (
		<>
			<div className="rounded-xl p-6 border border-slate-200 bg-white">
				{/* Free Trial Header */}
				<div className=" gap-2 mb-4">

					<h3 className="text-lg font-semibold text-black leading-tight text-balance mb-2">
					Arbeidsovereenkomsten & personeelsreglementen in een handomdraai
					</h3>
				</div>

				{/* Pricing Breakdown */}
				<div className="space-y-3">
					<div className="flex justify-between items-center text-sm">
						<span className="text-gray-600">DentHR</span>
						<span className="font-medium">
							<span className="text-xs opacity-50">€</span>
							{formatPrice(netPrice)}
						</span>
					</div>

					{planInfo?.discount && (
						<div className="flex justify-between items-center text-sm">
							<span className="text-gray-600">
								Korting
								{couponCode && (
									<span className="ml-1 text-gray-400">
										({couponCode})
									</span>
								)}
							</span>
							<span className="text-green-700">
								{planInfo.discount.type === "percent"
									? `-${planInfo.discount.percentOff}%`
									: `-€${formatPrice(planInfo.discount.amountOff || 0)}`}
							</span>
						</div>
					)}

					<div className="flex justify-between items-center text-sm">
						<span className="text-gray-600">Btw</span>
						<span className="text-gray-600">
							<span className="text-xs opacity-50">€</span>
							{formatPrice(taxAmount)}
						</span>
					</div>

					<div className="border-t pt-3">
						<div className="flex justify-between items-center">
							<span className="font-semibold text-green-800">
								{trialDays > 0 ? "Vandaag" : "Totaal"}
							</span>
							<span className="text-lg font-bold text-green-600">
								<span className="text-xs opacity-50 font-normal">
									€
								</span>
								{trialDays > 0
									? "0,00"
									: formatPrice(totalPrice)}
							</span>
						</div>
						{trialDays > 0 && (
							<div className="bg-blue-50/50 border border-blue-100 rounded-lg p-3 my-3 text-xs text-blue-900/80 leading-relaxed">
								Er wordt nu niets afgeschreven.
								{startDateLabel
									? ` Eerste afschrijving pas vanaf ${startDateLabel}.`
									: " De eerste afschrijving is pas na je gratis tijd."}
							</div>
						)}
						{trialDays > 0 && (
							<div className="flex justify-between items-center font-semibold text-slate-700">
								<span>
									{startDateLabel
										? `Vanaf ${startDateLabel}`
										: "Na gratis tijd"}
								</span>
								<span className="text-lg">
									<span className="text-xs opacity-70">
										€
									</span>
									{formatPrice(totalPrice)}
									<span className="text-xs opacity-50 font-normal">
										/jaar
									</span>
								</span>
							</div>
						)}
					</div>
				</div>
			</div>
			
			<div className="bg-slate-200/60 rounded-2xl p-6 !mt-10 ">
				<h4 className="font-semibold text-slate-900 mb-3 text-sm">
					Dit krijg je
				</h4>
				<ul className="space-y-2 text-sm text-slate-800">
					<li className="flex items-start gap-2">
						<CheckIcon className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
						<span>
							In een handomdraai je personeelsreglement
						</span>
					</li>
					<li className="flex items-start gap-2">
						<CheckIcon className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
						<span>Arbeidsovereenkomsten</span>
					</li>
					<li className="flex items-start gap-2">
						<CheckIcon className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
						<span>Overeenkomsten van opdracht</span>
					</li>
					<li className="flex items-start gap-2">
						<CheckIcon className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
						<span>Teamtevredenheidsonderzoek</span>
					</li><li className="flex items-start gap-2">
						<CheckIcon className="size-4 text-green-600 mt-0.5 flex-shrink-0" />
						<span>Jaarlijks webinar met updates in het arbeidsrecht</span>
					</li>
				</ul>

				{/* <div className="mt-4 pt-4 border-t border-slate-200">
					<p className="text-xs text-slate-700 leading-relaxed">
						<LockClosedIcon className="size-4 text-slate-600 mt-0.5 flex-shrink-0" />
						Geen risico Stop wanneer je wilt - geen opzegtermijn,
						geen gedoe
					</p>
				</div> */}

				{/* Security notice */}
			</div>
			{/* <div className="flex space-x-3 transform transition-all duration-300 ">
				<ShieldCheckIcon className="size-6 text-green-600 flex-shrink-0" />
				<div>
					<p className="text-sm font-medium text-green-800">
						Opzeggen kan altijd met één klik
					</p>
				</div>
			</div> */}
		</>
	);
};

export default FreeTrialOrderSummary;